import { useRef, useCallback, useEffect, useState } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

function countWords(html: string): number {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(w => w.length > 0).length;
}

export default function RichTextEditor({ value, onChange, placeholder, minHeight = '400px' }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
        setWordCount(countWords(value || ''));
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      onChange(html);
      setWordCount(countWords(html));
    }
  }, [onChange]);

  const exec = useCallback((command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  }, [handleInput]);

  const isActive = useCallback((command: string) => {
    return document.queryCommandState(command);
  }, []);

  const handleInsertLink = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString() || '';
    const url = window.prompt('Enter URL:', 'https://');
    if (url && url !== 'https://') {
      editorRef.current?.focus();
      if (selectedText) {
        document.execCommand('createLink', false, url);
      } else {
        const linkHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
      }
      handleInput();
    }
  }, [handleInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      exec('insertText', '    ');
    }
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b': e.preventDefault(); exec('bold'); break;
        case 'i': e.preventDefault(); exec('italic'); break;
        case 'u': e.preventDefault(); exec('underline'); break;
        case 'z': e.preventDefault(); exec('undo'); break;
        case 'y': e.preventDefault(); exec('redo'); break;
        case 'k': e.preventDefault(); handleInsertLink(); break;
      }
    }
  }, [exec, handleInsertLink]);

  const ToolButton = ({ cmd, icon, title, active }: { cmd: string; icon: string; title: string; active?: boolean }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        (active ?? isActive(cmd)) ? 'bg-ink-600/30 text-ink-400' : 'text-gray-400 hover:text-white hover:bg-gray-700'
      }`}
    >
      <span className="text-sm" dangerouslySetInnerHTML={{ __html: icon }} />
    </button>
  );

  const Divider = () => <div className="w-px h-6 bg-gray-700 mx-1" />;

  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden bg-gray-800 focus-within:border-ink-500 transition-colors">
      <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-gray-700 bg-gray-850">
        <ToolButton cmd="bold" icon="<b>B</b>" title="Bold (Ctrl+B)" />
        <ToolButton cmd="italic" icon="<i>I</i>" title="Italic (Ctrl+I)" />
        <ToolButton cmd="underline" icon="<u>U</u>" title="Underline (Ctrl+U)" />
        <ToolButton cmd="strikeThrough" icon="<s>S</s>" title="Strikethrough" />
        <Divider />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'h2'); }}
          title="Heading"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm font-bold">H</span>
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'h3'); }}
          title="Subheading"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <span className="text-xs font-bold">H2</span>
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'p'); }}
          title="Paragraph"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm">P</span>
        </button>
        <Divider />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'blockquote'); }}
          title="Block Quote"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
        <ToolButton cmd="insertUnorderedList" icon="&bull;" title="Bullet List" />
        <ToolButton cmd="insertOrderedList" icon="1." title="Numbered List" />
        <Divider />
        {/* Insert Link */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleInsertLink(); }}
          title="Insert Link (Ctrl+K)"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        <Divider />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('insertHorizontalRule'); }}
          title="Horizontal Rule / Scene Break"
          className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm">—</span>
        </button>
        <ToolButton cmd="justifyLeft" icon="≡" title="Align Left" />
        <ToolButton cmd="justifyCenter" icon="≡" title="Align Center" />
        <Divider />
        <ToolButton cmd="undo" icon="↶" title="Undo (Ctrl+Z)" />
        <ToolButton cmd="redo" icon="↷" title="Redo (Ctrl+Y)" />
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="px-6 py-4 font-serif text-lg leading-relaxed text-gray-200 outline-none overflow-y-auto prose prose-invert max-w-none
          [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3
          [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
          [&_blockquote]:border-l-4 [&_blockquote]:border-ink-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400 [&_blockquote]:my-4
          [&_hr]:border-gray-700 [&_hr]:my-6
          [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6
          [&_a]:text-ink-400 [&_a]:underline [&_a]:hover:text-ink-300
          [&_p]:mb-3"
        style={{ minHeight }}
        data-placeholder={placeholder || 'Begin writing your story...'}
        suppressContentEditableWarning
      />

      {/* Word Count Footer */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-gray-700 bg-gray-850">
        <span className="text-xs text-gray-600">Tip: Ctrl+B bold · Ctrl+I italic · Ctrl+K link · Ctrl+Z undo</span>
        <span className="text-xs text-gray-500">
          <span className={wordCount > 0 ? 'text-gray-400' : ''}>{wordCount.toLocaleString()}</span>
          {' '}words
        </span>
      </div>
    </div>
  );
}
